// The lead-routing API contracts shown in the API dialog. Hand-written and
// kept honest against the controllers; each entry is one endpoint with a
// realistic example body and response. Machine endpoints authenticate with
// the shared API key, console endpoints with a user token carrying a sales
// role (career consultants may only call my-routing).

export type ApiContract = {
  key: string
  tag: string
  method: "GET" | "POST" | "PUT"
  base: "lms" | "campus"
  path: string
  summary: string
  auth: string
  exampleBody?: unknown
  responseStatus: number
  exampleResponse: unknown
}

export const API_CONTRACTS: ApiContract[] = [
  {
    key: "allocate",
    tag: "Routing",
    method: "POST",
    base: "lms",
    path: "/api/v1/allocations/allocate-consultant-to-lead",
    summary:
      "Pick a consultant for a new lead. Runs the published rules when the mode is Rules, rotation otherwise. Returns the consultant's HubSpot contact id; the caller writes the owner and then confirms.",
    auth: "Bearer API key",
    exampleBody: {
      lead: {
        id: "3f8a1c0e-6d24-4e6f-9a71-c5b90d1f4e28",
        qtfScore: 2,
        englishLevel: 4,
        germanLevel: 6,
        fieldOfStudyInterest: "Cyber",
        germanyResidencyStatus: "citizen",
        techRelatedExperience: "Intermediate",
        advisorMeetingStatus: "already_had_meeting",
        utmSource: "google",
        propertiesSource: "hubspot",
        propertiesSyncedAt: "2026-08-27T10:15:00Z",
      },
    },
    responseStatus: 201,
    exampleResponse: "201",
  },
  {
    key: "rotate",
    tag: "Routing",
    method: "POST",
    base: "lms",
    path: "/api/v1/allocations/rotate-lead-to-consultant",
    summary:
      "Rotate a lead to the next consultant on an active shift and record the allocation. Rules do not run on this path.",
    auth: "Bearer API key",
    exampleBody: { leadId: "512001234567" },
    responseStatus: 201,
    exampleResponse: {
      id: "37a78d15-81c2-42c6-ab01-e43e44626f58",
      name: "Ron Revensari",
      hubspotContactId: "201",
    },
  },
  {
    key: "confirm",
    tag: "Routing",
    method: "POST",
    base: "lms",
    path: "/api/v1/allocations/confirm-lead-assignment",
    summary:
      "Confirm that a routed lead's owner write really happened in HubSpot. Keeps the fairness counter honest; call it after the owner write succeeds.",
    auth: "Bearer API key",
    exampleBody: { leadId: "3f8a1c0e-6d24-4e6f-9a71-c5b90d1f4e28" },
    responseStatus: 201,
    exampleResponse: true,
  },
  {
    key: "campus-assign",
    tag: "Campus webhook",
    method: "POST",
    base: "campus",
    path: "/api/bg-leads/assign-lead-to-consultant",
    summary:
      "HubSpot-workflow entry point on campus. Directed (consultant named) or undirected (rules pick). An undirected call routes on the lead's current HubSpot state; a workflow may send current property values in `properties` and they win over the read.",
    auth: "None today (contained endpoint; callers are HubSpot workflows)",
    exampleBody: {
      lead_email: "lead@example.com",
      trigger: "reassign-leads-2.1",
      properties: {
        bg___qualification_by_typeform: "3",
        bg0___german_language_skill_level: "Mother tongue",
        bg___field_of_interest: "Cyber Security",
      },
    },
    responseStatus: 201,
    exampleResponse: {
      status: "assigned",
      ownerId: "82014025",
      taskContext: "reassignment",
    },
  },
  {
    key: "rules",
    tag: "Console",
    method: "GET",
    base: "lms",
    path: "/api/v1/lead-routing/rules",
    summary: "All routing rules, drafts included.",
    auth: "User token · sales_admin",
    responseStatus: 200,
    exampleResponse: [
      {
        id: "49a18484-fc78-4e66-bf05-7d9bcbca7e24",
        name: "German QTF 1-4",
        priority: 1,
        published: true,
        hasUnpublishedChanges: false,
        isFallback: false,
        conditions: [{ property: "qtf", operator: "in", value: [1, 2, 3, 4] }],
        weights: [
          {
            consultantId: "37a78d15-81c2-42c6-ab01-e43e44626f58",
            weight: 60,
          },
        ],
      },
    ],
  },
  {
    key: "settings",
    tag: "Console",
    method: "PUT",
    base: "lms",
    path: "/api/v1/lead-routing/settings",
    summary:
      "Switch the assignment mode. enabled true = Rules, false = Rotation. Takes effect immediately.",
    auth: "User token · sales_admin",
    exampleBody: { enabled: true },
    responseStatus: 200,
    exampleResponse: {
      enabled: true,
      windowDays: 7,
      updatedBy: "1502052e-b149-44b9-9924-da6003df37ca",
      updatedAt: "2026-08-27T10:15:00Z",
    },
  },
  {
    key: "rule-lead-counts",
    tag: "Console",
    method: "GET",
    base: "lms",
    path: "/api/v1/lead-routing/rule-lead-counts?from=2026-07-28&to=2026-08-27",
    summary:
      "How many routed leads each rule's split actually took in the date range, from the routing log. Nothing simulated.",
    auth: "User token · sales_admin",
    responseStatus: 200,
    exampleResponse: {
      total: 3,
      everyoneElse: 1,
      rules: [{ ruleId: "49a18484-fc78-4e66-bf05-7d9bcbca7e24", count: 2 }],
    },
  },
  {
    key: "recent-leads",
    tag: "Console",
    method: "GET",
    base: "lms",
    path: "/api/v1/lead-routing/recent-leads?limit=8",
    summary:
      "Recently routed leads: matched rule, consultant and value provenance. No lead PII.",
    auth: "User token · sales_admin",
    responseStatus: 200,
    exampleResponse: [
      {
        leadId: "3f8a1c0e-6d24-4e6f-9a71-c5b90d1f4e28",
        routedAt: "2026-08-27T10:15:00Z",
        source: "weighted",
        ruleName: "German QTF 1-4",
        consultantName: "Ron Revensari",
        snapshot: {
          qtfScore: 2,
          fieldOfStudyInterest: "Cyber",
          propertiesSource: "hubspot",
          propertiesSyncedAt: "2026-08-27T10:14:58Z",
        },
      },
    ],
  },
  {
    key: "resync",
    tag: "Console",
    method: "POST",
    base: "lms",
    path: "/api/v1/lead-routing/resync-lead-sample",
    summary:
      "Refresh the recent-lead sample with each lead's current HubSpot state, read through campus. 503 when HubSpot is unreachable.",
    auth: "User token · sales_admin",
    responseStatus: 201,
    exampleResponse: { leadsSynced: 412, syncedAt: "2026-08-27T10:15:00Z" },
  },
  {
    key: "my-routing",
    tag: "Console",
    method: "GET",
    base: "lms",
    path: "/api/v1/my-routing",
    summary:
      "The logged-in consultant's own standing: shift, availability, rules naming them, received leads. The only lead-routing endpoint career consultants can call.",
    auth: "User token · career_consultant, sales_team_lead, sales_admin or lms_admin",
    responseStatus: 200,
    exampleResponse: {
      consultant: {
        name: "Anne Garcia",
        isAvailable: true,
        acceptedQtfs: [1, 2, 3, 4],
        languages: ["German", "English"],
      },
      onShiftNow: true,
      minutesToNextShift: null,
      rules: [
        {
          ruleId: "49a18484-fc78-4e66-bf05-7d9bcbca7e24",
          ruleName: "German QTF 1-4",
          targetPct: 60,
        },
      ],
      receivedLast7Days: 12,
      recent: [
        {
          routedAt: "2026-08-27T09:20:00Z",
          source: "weighted",
          ruleName: "German QTF 1-4",
          confirmed: true,
        },
      ],
    },
  },
]

export function contractUrl(contract: ApiContract): string {
  if (contract.base === "campus") {
    return `https://app.masterschool.com${contract.path}`
  }
  return `${window.location.origin}${contract.path}`
}

export function curlSnippet(contract: ApiContract): string {
  const lines = [`curl -X ${contract.method} \\`]
  lines.push(`  '${contractUrl(contract)}' \\`)
  lines.push(`  -H 'Authorization: Bearer <token>' \\`)
  lines.push(`  -H 'Content-Type: application/json'`)
  if (contract.exampleBody !== undefined) {
    lines[lines.length - 1] += " \\"
    lines.push(`  -d '${JSON.stringify(contract.exampleBody, null, 2)}'`)
  }
  return lines.join("\n")
}

export function fetchSnippet(contract: ApiContract): string {
  const options: string[] = [
    `  method: "${contract.method}",`,
    `  headers: {`,
    `    Authorization: "Bearer <token>",`,
    `    "Content-Type": "application/json",`,
    `  },`,
  ]
  if (contract.exampleBody !== undefined) {
    const body = JSON.stringify(contract.exampleBody, null, 2).replace(
      /\n/g,
      "\n  ",
    )
    options.push(`  body: JSON.stringify(${body}),`)
  }
  return `await fetch("${contractUrl(contract)}", {\n${options.join("\n")}\n})`
}
